import type { ReactNode } from "react"
import { useRef, useState } from "react"
import {
	IconBellOutline18 as Bell,
	IconBellSlashOutline18 as BellOff,
	IconSquaresConnectedOutline18 as Channels,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
	IconDoorOpenOutline18 as DoorOpen,
	IconEyeOutline18 as Eye,
	IconLayersStackedOutline18 as LayersStacked,
	IconChatBubbleQuoteOutline18 as MessageSquareQuoteIcon,
	IconMessageUserOutline18 as MessageUser,
	IconPaperPlane2Outline18 as PaperPlane,
	IconBracketsCurlyOutline18 as Variable,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Alert,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuItem,
	IconButton,
	IntegrationTemplate,
	SegmentedControl,
	Select,
	SelectTrigger,
	Switch,
	Textarea,
} from "@productlane/ui"
import { SlackIcon } from "@productlane/ui/src/icons/slack-logo"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { IntegrationLayout } from "../../settings/integrations/integration-layout"
import { GROUPING_OPTIONS } from "../../settings/integrations/slack-grouping"
import { SETTINGS_MAIN_CURSOR_CLASS } from "../../settings/settings-column"
import { SettingsHeader } from "../../settings/settings-header"
import { HowItWorksButton } from "../../settings/settings-switches/how-it-works-button"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import {
	SETTINGS_PAGE_PADDING_X_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import { DesignIssueHeader } from "./design-issue-header"

const DEFAULT_CHANNEL_MESSAGE =
	"Hi {name}, welcome to {channel}. Post your question here and our team replies in this channel."

const SAMPLE_COMMUNITY_MESSAGE =
	"Hi {name}, welcome to the {workspace} community.\n\n*Where to start*\n- Ask questions in #help\n- Share ideas in #product-feedback\n- Read what's new in #announcements"

const VARIABLES = [
	{ key: "{name}", label: "First name", sample: "Alex" },
	{ key: "{channel}", label: "Channel", sample: "#ext-acme" },
	{ key: "{workspace}", label: "Slack workspace", sample: "Acme" },
]

const DELIVERY_OPTIONS = [
	{ value: "ephemeral", label: "In the channel, only they see it" },
	{ value: "dm", label: "Direct message" },
]

const CHANNELS = [
	"ext-acme",
	"ext-globex",
	"ext-initech",
	"ext-umbrella",
	"help",
	"product-feedback",
]

interface BotIdentity {
	name: string
	logoUrl?: string
}

// /design routes render outside the ZeroProvider, so the workspace is mocked. The real build reads it from Zero.
const MOCK_WORKSPACE = {
	name: "Productlane",
	officeHours: [] as unknown[],
	logoUrl:
		"https://uploads.productlane.com/49b12200975e7b1a3aa83d196217d02d.png",
}

function useBotIdentity(): BotIdentity {
	return MOCK_WORKSPACE
}

function fillVariables(
	text: string,
	name: string,
	channel: string,
	workspace: string,
) {
	return text
		.replaceAll("{name}", name)
		.replaceAll("{channel}", channel)
		.replaceAll("{workspace}", workspace)
}

// Enough Slack mrkdwn for a faithful preview: *bold*, _italic_, <url|text>, #channel, "- " lists.
function renderInline(text: string): ReactNode[] {
	const parts = text.split(/(\*[^*]+\*|_[^_]+_|<[^>|]+\|[^>]+>|#[a-z0-9-]+)/g)
	return parts.map((part, i) => {
		if (/^\*[^*]+\*$/.test(part))
			return <strong key={i}>{part.slice(1, -1)}</strong>
		if (/^_[^_]+_$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>
		const link = /^<([^>|]+)\|([^>]+)>$/.exec(part)
		if (link)
			return (
				<span key={i} className="text-[#1264a3]">
					{link[2]}
				</span>
			)
		if (/^#[a-z0-9-]+$/.test(part))
			return (
				<span key={i} className="rounded-sm bg-[#e8f5fa] px-0.5 text-[#1264a3]">
					{part}
				</span>
			)
		return part
	})
}

function SlackMrkdwn({ text }: { text: string }) {
	const lines = text.split("\n")
	return (
		<div className="space-y-0.5">
			{lines.map((line, i) =>
				line.startsWith("- ") ? (
					<div key={i} className="flex gap-2 pl-1">
						<span>•</span>
						<span>{renderInline(line.slice(2))}</span>
					</div>
				) : (
					<div key={i} className="min-h-[1lh]">
						{renderInline(line)}
					</div>
				),
			)}
		</div>
	)
}

function SlackBotMessage({
	text,
	ephemeral,
}: {
	text: string
	ephemeral?: boolean
}) {
	const bot = useBotIdentity()
	return (
		<div className="flex gap-2 px-5 py-2">
			{bot.logoUrl ? (
				<img
					src={bot.logoUrl}
					alt=""
					className="mt-0.5 size-9 flex-none rounded-md object-cover"
				/>
			) : (
				<div className="mt-0.5 flex size-9 flex-none items-center justify-center rounded-md bg-[#4a154b] text-sm font-bold text-white">
					{bot.name.charAt(0).toUpperCase()}
				</div>
			)}
			<div className="min-w-0 flex-1">
				{ephemeral && (
					<div className="mb-0.5 flex items-center gap-1 text-[13px] text-[#616061]">
						<Eye className="size-3.5 flex-none" />
						Only visible to you
					</div>
				)}
				<div className="flex items-baseline gap-1.5">
					<span className="text-[15px] font-bold text-[#1d1c1d]">
						{bot.name}
					</span>
					<span className="rounded-sm bg-[#1d1c1d]/[0.08] px-1 text-[10px] font-semibold text-[#616061]">
						APP
					</span>
					<span className="text-xs text-[#616061]">10:42 AM</span>
				</div>
				<div className="text-[15px] leading-[22px] text-[#1d1c1d]">
					<SlackMrkdwn text={text} />
				</div>
			</div>
		</div>
	)
}

function SlackWindow({
	header,
	subheader,
	children,
}: {
	header: ReactNode
	subheader?: ReactNode
	children: ReactNode
}) {
	return (
		<div className="overflow-hidden rounded-lg bg-white font-[Lato,ui-sans-serif,system-ui] shadow-md ring-1 ring-black/5 dark:ring-white/10">
			<div className="flex h-12 items-center gap-2 border-b border-gray-200 px-5">
				<span className="text-[15px] font-bold text-[#1d1c1d]">{header}</span>
				{subheader}
			</div>
			<div className="pb-4 pt-2">{children}</div>
		</div>
	)
}

function JoinLine({ name, channel }: { name: string; channel: string }) {
	return (
		<div className="flex gap-2 px-5 py-2">
			<div className="mt-0.5 flex size-9 flex-none items-center justify-center rounded-md bg-[#e8912d] text-sm font-bold text-white">
				{name.slice(0, 1)}
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-baseline gap-1.5">
					<span className="text-[15px] font-bold text-[#1d1c1d]">
						{name} Rivera
					</span>
					<span className="text-xs text-[#616061]">10:42 AM</span>
				</div>
				<div className="text-[15px] text-[#616061]">joined #{channel}.</div>
			</div>
		</div>
	)
}

function MessageEditor({
	value,
	onChange,
}: {
	value: string
	onChange: (value: string) => void
}) {
	const ref = useRef<HTMLTextAreaElement>(null)
	const insert = (key: string) => {
		const el = ref.current
		const start = el?.selectionStart ?? value.length
		const end = el?.selectionEnd ?? value.length
		onChange(value.slice(0, start) + key + value.slice(end))
	}
	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium text-primary">Message</span>
				<DropdownMenu
					trigger={
						<Button variant="ghost" Icon={Variable}>
							Insert variable
						</Button>
					}
					contentProps={{ align: "end" }}
				>
					{VARIABLES.map((v) => (
						<DropdownMenuItem key={v.key} onSelect={() => insert(v.key)}>
							<span className="flex w-full items-center justify-between gap-6">
								{v.label}
								<span className="font-mono text-xs text-tertiary">{v.key}</span>
							</span>
						</DropdownMenuItem>
					))}
				</DropdownMenu>
			</div>
			<Textarea
				ref={ref}
				name="message"
				value={value}
				minRows={3}
				onChange={(e) => onChange(e.target.value)}
			/>
			<div className="flex justify-between text-xs text-tertiary">
				<span>Slack formatting works: *bold*, _italic_, lists.</span>
				<span>Saved</span>
			</div>
		</div>
	)
}

function PreviewCaption({ enabled }: { enabled: boolean }) {
	return (
		<div className="text-xs text-tertiary">
			{enabled
				? "Preview of what new members see"
				: "Preview of what new members see once you turn this on"}
		</div>
	)
}

interface WelcomeState {
	channelOn: boolean
	setChannelOn: (on: boolean) => void
	communityOn: boolean
	setCommunityOn: (on: boolean) => void
	channelMessage: string
	setChannelMessage: (value: string) => void
	communityMessage: string
	setCommunityMessage: (value: string) => void
	channels: string[]
	setChannels: (value: string[]) => void
	delivery: string
	setDelivery: (value: string) => void
}

function useWelcomeState(): WelcomeState {
	const [channelOn, setChannelOn] = useState(true)
	const [communityOn, setCommunityOn] = useState(false)
	const [channelMessage, setChannelMessage] = useState(DEFAULT_CHANNEL_MESSAGE)
	const [communityMessage, setCommunityMessage] = useState(
		SAMPLE_COMMUNITY_MESSAGE,
	)
	const [channels, setChannels] = useState<string[]>([])
	const [delivery, setDelivery] = useState("ephemeral")
	return {
		channelOn,
		setChannelOn,
		communityOn,
		setCommunityOn,
		channelMessage,
		setChannelMessage,
		communityMessage,
		setCommunityMessage,
		channels,
		setChannels,
		delivery,
		setDelivery,
	}
}

function SendTest() {
	return (
		<div className="flex items-center gap-3">
			<Button variant="outline" Icon={PaperPlane}>
				Send test
			</Button>
			<span className="text-xs text-tertiary">
				Sends it to your linked Slack account.
			</span>
		</div>
	)
}

function ChannelSwitchRow({ s }: { s: WelcomeState }) {
	return (
		<SettingItem
			Icon={DoorOpen}
			title="Channel welcome"
			description="Greets new people in your channels."
			action={
				<Switch
					name="channel-welcome"
					checked={s.channelOn}
					onCheckedChange={s.setChannelOn}
				/>
			}
		/>
	)
}

function CommunitySwitchRow({ s }: { s: WelcomeState }) {
	return (
		<SettingItem
			Icon={MessageUser}
			title="Community welcome"
			description="DMs everyone who joins this Slack workspace."
			action={
				<Switch
					name="community-welcome"
					checked={s.communityOn}
					onCheckedChange={s.setCommunityOn}
				/>
			}
		/>
	)
}

function ChannelFields({ s }: { s: WelcomeState }) {
	return (
		<div className="space-y-4">
			<MessageEditor value={s.channelMessage} onChange={s.setChannelMessage} />
			<SettingItem
				Icon={Channels}
				title="Channels"
				description="Muted channels still get the welcome."
				action={
					<DropdownMenu
						trigger={
							<SelectTrigger className="w-44 dark:bg-gray-700/70">
								{s.channels.length === 0
									? "All channels"
									: `${s.channels.length} channels`}
							</SelectTrigger>
						}
						contentProps={{ align: "end" }}
						className="max-h-72 overflow-y-auto"
					>
						{CHANNELS.map((c) => (
							<DropdownMenuCheckboxItem
								key={c}
								checked={s.channels.includes(c)}
								onSelect={(e) => e.preventDefault()}
								onCheckedChange={(checked) =>
									s.setChannels(
										checked
											? [...s.channels, c]
											: s.channels.filter((x) => x !== c),
									)
								}
							>
								#{c}
							</DropdownMenuCheckboxItem>
						))}
					</DropdownMenu>
				}
			/>
			<SettingItem
				Icon={Eye}
				title="Delivery"
				description="Who sees the welcome, and where."
				action={
					<Select
						name="delivery"
						value={s.delivery}
						options={DELIVERY_OPTIONS}
						onChange={s.setDelivery}
						className="w-64"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			{s.delivery === "dm" && (
				<Alert
					variant="default"
					header="Reconnect Slack to send DMs"
					icon={PaperPlane}
				>
					<div className="flex items-start justify-between gap-4">
						<span>DMs need one more Slack permission.</span>
						<Button variant="outline">Reconnect</Button>
					</div>
				</Alert>
			)}
			<SendTest />
		</div>
	)
}

function CommunityFields({ s }: { s: WelcomeState }) {
	return (
		<div className="space-y-4">
			<MessageEditor
				value={s.communityMessage}
				onChange={s.setCommunityMessage}
			/>
			<SendTest />
		</div>
	)
}

function ChannelPreview({ s }: { s: WelcomeState }) {
	const bot = useBotIdentity()
	return (
		<div className="space-y-2">
			<PreviewCaption enabled={s.channelOn} />
			<SlackWindow
				header="# ext-acme"
				subheader={
					<Badge size="sm" variant="light-gray">
						Shared with Acme
					</Badge>
				}
			>
				<JoinLine name="Alex" channel="ext-acme" />
				<SlackBotMessage
					text={fillVariables(s.channelMessage, "Alex", "#ext-acme", bot.name)}
					ephemeral={s.delivery === "ephemeral"}
				/>
			</SlackWindow>
		</div>
	)
}

function CommunityPreview({ s }: { s: WelcomeState }) {
	const bot = useBotIdentity()
	return (
		<div className="space-y-2">
			<PreviewCaption enabled={s.communityOn} />
			<SlackWindow header={bot.name}>
				<SlackBotMessage
					text={fillVariables(s.communityMessage, "Sam", "", bot.name)}
				/>
			</SlackWindow>
		</div>
	)
}

// Version A: switch, fields only while on, preview always below.
function ChannelWelcomeStacked({ s }: { s: WelcomeState }) {
	return (
		<SettingsContainer>
			<ChannelSwitchRow s={s} />
			<div className={s.channelOn ? "pt-4" : "hidden"}>
				<ChannelFields s={s} />
			</div>
			<div className="pt-6">
				<ChannelPreview s={s} />
			</div>
		</SettingsContainer>
	)
}

function CommunityWelcomeStacked({ s }: { s: WelcomeState }) {
	return (
		<SettingsContainer>
			<CommunitySwitchRow s={s} />
			<div className={s.communityOn ? "pt-4" : "hidden"}>
				<CommunityFields s={s} />
			</div>
			<div className="pt-6">
				<CommunityPreview s={s} />
			</div>
		</SettingsContainer>
	)
}

function NewBadge() {
	return (
		<Badge size="sm" variant="dark-gray">
			New
		</Badge>
	)
}

const DISABLE_VALUE = "__disable__"

const NOTIFICATION_CHANNEL_OPTIONS = [
	{ value: DISABLE_VALUE, label: "Disable" },
	...[...CHANNELS, "support-alerts"]
		.sort()
		.map((c) => ({ value: c, label: c })),
]

// Replica of the settings layout around a page: page background, the ghost back
// link the layout derives from the parent route, and the default 704px column.
function SettingsFrame({
	breadcrumb,
	isNew,
	back,
	columnClassName = "max-w-[704px]",
	children,
}: {
	breadcrumb: string
	isNew?: boolean
	back: string
	columnClassName?: string
	children: ReactNode
}) {
	return (
		<section className="space-y-2">
			<div className="flex items-center gap-2 text-xs font-medium text-tertiary">
				{breadcrumb}
				{isNew && <NewBadge />}
			</div>
			<div className="overflow-hidden rounded-xl bg-gray-25 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
				<div className="block px-2 pt-2 sm:px-8 sm:pt-4">
					<Button variant="ghost" Icon={ChevronLeft}>
						{back}
					</Button>
				</div>
				<main
					className={cn(
						"mx-auto flex w-full flex-col px-5 py-8 sm:py-16",
						columnClassName,
						SETTINGS_MAIN_CURSOR_CLASS,
						SETTINGS_PAGE_PADDING_X_CLASS,
					)}
				>
					<SettingsCardInsetContext.Provider value={true}>
						{children}
					</SettingsCardInsetContext.Provider>
				</main>
			</div>
		</section>
	)
}

function welcomeStatus(channelOn: boolean, communityOn: boolean) {
	const count = Number(channelOn) + Number(communityOn)
	return count === 0 ? "Off" : `${count} on`
}

// Faithful copy of SlackIntegrationPage + SlackForm as they render when connected.
function SlackIntegrationReplica({
	optionRows,
	section,
}: {
	optionRows?: ReactNode
	section?: ReactNode
}) {
	const workspace = MOCK_WORKSPACE
	const [grouping, setGrouping] = useState("SINGLE:30")
	const [notificationChannelId, setNotificationChannelId] =
		useState("support-alerts")
	const [mutedInboxChannelIds, setMutedInboxChannelIds] = useState<string[]>([])
	const [pushBotReplyChannel, setPushBotReplyChannel] = useState(true)

	return (
		<>
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<SlackIcon className="w-10" />}
				name={
					<div className="flex flex-wrap items-center gap-2">
						Slack
						<Badge
							variant="green"
							size="sm"
							className="!text-green-700 dark:!text-green-400"
						>
							<span className="size-1.5 rounded-full bg-green-500" />
							Connected
						</Badge>
					</div>
				}
				description="Sync conversations from Slack."
				button={
					<div className="flex items-center gap-2">
						<HowItWorksButton
							article="eba421f3-5a25-48cc-88a3-2658544b24fa"
							variant="icon"
						/>
						<Button variant="outline">Disconnect</Button>
					</div>
				}
				instructions={
					<>
						<div className="space-y-8">
							<div className="space-y-4">
								<div className="text-sm font-medium">Options</div>
								<SettingsContainer>
									<SettingItem
										Icon={LayersStacked}
										title="Message grouping"
										description="How long a channel's messages keep joining one thread."
										action={
											<Select
												name="grouping"
												value={grouping}
												options={GROUPING_OPTIONS}
												onChange={setGrouping}
												className="w-48"
												selectClassName="dark:bg-gray-700/70"
											/>
										}
									/>
									<SettingItem
										className="pt-4"
										Icon={Bell}
										title="Notifications"
										description="New threads will be sent to this channel."
										action={
											<Select
												name="notificationChannelId"
												value={notificationChannelId}
												options={NOTIFICATION_CHANNEL_OPTIONS}
												onChange={setNotificationChannelId}
												selectClassName="dark:bg-gray-700/70"
											/>
										}
									/>
									<SettingItem
										className="pt-4"
										Icon={BellOff}
										title="Muted channels"
										description="The inbox skips these channels. Changelog broadcasts still go out."
										action={
											<DropdownMenu
												trigger={
													<SelectTrigger className="w-40 dark:bg-gray-700/70">
														{mutedInboxChannelIds.length === 0
															? "None"
															: `${mutedInboxChannelIds.length} muted`}
													</SelectTrigger>
												}
												contentProps={{ align: "end" }}
												className="max-h-72 overflow-y-auto"
											>
												{[...CHANNELS].sort().map((c) => (
													<DropdownMenuCheckboxItem
														key={c}
														checked={mutedInboxChannelIds.includes(c)}
														onSelect={(e) => e.preventDefault()}
														onCheckedChange={(checked) =>
															setMutedInboxChannelIds(
																checked
																	? [...mutedInboxChannelIds, c]
																	: mutedInboxChannelIds.filter(
																			(id) => id !== c,
																		),
															)
														}
													>
														#{c}
													</DropdownMenuCheckboxItem>
												))}
											</DropdownMenu>
										}
									/>
									{optionRows}
								</SettingsContainer>
							</div>

							{section}

							<div className="space-y-4">
								<div className="text-sm font-medium">Action Menu</div>
								<SettingsContainer>
									<SettingItem
										Icon={MessageSquareQuoteIcon}
										title="Thread auto-replies"
										description={
											<div className="flex flex-col items-start gap-2">
												The bot automatically replies in Slack threads depending
												on your office hours and availability.
												{!workspace?.officeHours?.length && (
													<Button variant="link">
														Configure office hours{" "}
														<ChevronRight className="h-3 w-3" />
													</Button>
												)}
											</div>
										}
										action={
											<Switch
												name="pushBotReplyChannel"
												checked={pushBotReplyChannel}
												onCheckedChange={setPushBotReplyChannel}
											/>
										}
									/>
								</SettingsContainer>
							</div>
						</div>

						<SettingsContainer className="mt-4">
							<div className="space-y-4">
								<div className="text-sm">
									<div className="flex items-center justify-between">
										<div className="cursor-default">
											<p className="font-medium">
												Personal Slack notifications
											</p>
											<p className="mt-1 text-secondary">
												Set up personal Slack notifications for new assignments
												or comment mentions.
											</p>
										</div>
										<Button variant="default">Set up</Button>
									</div>
								</div>
							</div>
						</SettingsContainer>
					</>
				}
			/>
		</>
	)
}

function onOff(on: boolean) {
	return on ? "On" : "Off"
}

function LinkRow({
	Icon,
	title,
	description,
	status,
	onOpen,
}: {
	Icon: typeof DoorOpen
	title: string
	description: string
	status: string
	onOpen: () => void
}) {
	return (
		<div
			role="link"
			tabIndex={0}
			onClick={onOpen}
			onKeyDown={(e) => {
				if (e.key === "Enter") onOpen()
			}}
			className="pt-4"
		>
			<SettingItem
				Icon={Icon}
				title={
					<span className="flex items-center gap-2">
						{title}
						<NewBadge />
					</span>
				}
				description={description}
				action={
					<span className="flex flex-none items-center gap-3">
						<span className="min-w-8 text-right text-sm text-tertiary">
							{status}
						</span>
						<ChevronRight className="h-3 min-h-3 w-3 min-w-3 flex-none text-tertiary" />
					</span>
				}
			/>
		</div>
	)
}

type WelcomeTab = "channel" | "community"

const WELCOME_TABS = [
	{ value: "channel", label: "Channel welcome", Icon: DoorOpen },
	{ value: "community", label: "Community welcome", Icon: MessageUser },
] as const

// Version B: one welcome at a time, editor left and preview right.
function WelcomeSplitSubpage({
	s,
	tab,
	setTab,
}: {
	s: WelcomeState
	tab: WelcomeTab
	setTab: (tab: WelcomeTab) => void
}) {
	return (
		<div className="space-y-6">
			<SettingsHeader
				title="Welcome messages"
				titleSuffix={<NewBadge />}
				subtitle="Greet people who join your shared channels or Slack workspace."
			/>
			<SegmentedControl
				name="welcome-tab"
				value={tab}
				options={WELCOME_TABS}
				onValueChange={setTab}
				showLabels
			/>
			<div className="grid grid-cols-2 items-start gap-8">
				<SettingsContainer>
					{tab === "channel" ? (
						<>
							<ChannelSwitchRow s={s} />
							<div className="pt-4">
								<ChannelFields s={s} />
							</div>
						</>
					) : (
						<>
							<CommunitySwitchRow s={s} />
							<div className="pt-4">
								<CommunityFields s={s} />
							</div>
						</>
					)}
				</SettingsContainer>
				<div className="sticky top-6">
					{tab === "channel" ? (
						<ChannelPreview s={s} />
					) : (
						<CommunityPreview s={s} />
					)}
				</div>
			</div>
		</div>
	)
}

// Version C: rows that expand in place on the Slack page.
function ExpandableWelcome({
	row,
	open,
	onToggle,
	enabled,
	fields,
	preview,
}: {
	row: ReactNode
	open: boolean
	onToggle: () => void
	enabled: boolean
	fields: ReactNode
	preview: ReactNode
}) {
	return (
		<div className="pt-4 first:pt-0">
			<div className="flex items-start gap-2">
				<div className="min-w-0 flex-1">{row}</div>
				<IconButton
					variant="ghost"
					Icon={ChevronDown}
					aria-label={open ? "Collapse" : "Expand"}
					onClick={onToggle}
					className={cn("flex-none", open && "rotate-180")}
				/>
			</div>
			<div className={open ? "space-y-6 pb-2 pl-7 pt-4" : "hidden"}>
				{enabled && fields}
				{preview}
			</div>
		</div>
	)
}

function WelcomeInlineSection({
	s,
	openRow,
	setOpenRow,
}: {
	s: WelcomeState
	openRow: WelcomeTab | null
	setOpenRow: (row: WelcomeTab | null) => void
}) {
	const toggle = (row: WelcomeTab) => setOpenRow(openRow === row ? null : row)
	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2 text-sm font-medium">
				Welcome messages
				<NewBadge />
			</div>
			<SettingsContainer>
				<ExpandableWelcome
					row={<ChannelSwitchRow s={s} />}
					open={openRow === "channel"}
					onToggle={() => toggle("channel")}
					enabled={s.channelOn}
					fields={<ChannelFields s={s} />}
					preview={<ChannelPreview s={s} />}
				/>
				<ExpandableWelcome
					row={<CommunitySwitchRow s={s} />}
					open={openRow === "community"}
					onToggle={() => toggle("community")}
					enabled={s.communityOn}
					fields={<CommunityFields s={s} />}
					preview={<CommunityPreview s={s} />}
				/>
			</SettingsContainer>
		</div>
	)
}

const COLUMN_A = "w-[880px]"
const COLUMN_B = "w-[1280px]"
const COLUMN_C = "w-[880px]"

function VersionLabel({
	className,
	name,
	line,
}: {
	className: string
	name: string
	line: string
}) {
	return (
		<div className={cn("flex-none space-y-1", className)}>
			<div className="text-base font-semibold text-primary">{name}</div>
			<div className="text-sm text-secondary">{line}</div>
		</div>
	)
}

function Row({ children }: { children: ReactNode }) {
	return <div className="flex flex-nowrap items-start gap-10">{children}</div>
}

function Cell({
	className,
	children,
}: {
	className: string
	children: ReactNode
}) {
	return <div className={cn("flex-none", className)}>{children}</div>
}

export function SlackWelcomeMessagesPage() {
	const a = useWelcomeState()
	const b = useWelcomeState()
	const c = useWelcomeState()
	const [bTab, setBTab] = useState<WelcomeTab>("channel")
	const [cOpenTop, setCOpenTop] = useState<WelcomeTab | null>(null)
	const [cOpenBottom, setCOpenBottom] = useState<WelcomeTab | null>("channel")
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="min-h-screen bg-gray-50 px-10 py-10 pb-24 dark:bg-gray-950">
			<div className="max-w-4xl">
				<DesignIssueHeader
					identifier="PRO-10809"
					title="Welcome message when someone joins a connected Slack channel or the Slack community"
					url="https://linear.app/productlane/issue/PRO-10809"
					summary="Linear wants new people greeted in Slack: customers who join a shared channel, and members who join their community workspace. Each welcome gets its own text, a live Slack preview, and a test send. Both start off, and each person gets a welcome only once. Three layouts below, side by side; read a column top to bottom."
				/>
			</div>
			<div className="mt-10 overflow-x-auto pb-6">
				<div className="w-max space-y-8">
					<Row>
						<VersionLabel
							className={COLUMN_A}
							name="Version A"
							line="One row on the Slack page opens a page with both welcomes stacked."
						/>
						<VersionLabel
							className={COLUMN_B}
							name="Version B"
							line="Two rows on the Slack page; each opens one welcome with the editor left and the preview right."
						/>
						<VersionLabel
							className={COLUMN_C}
							name="Version C"
							line="No extra page. Both welcomes sit on the Slack page and expand in place."
						/>
					</Row>
					<Row>
						<Cell className={COLUMN_A}>
							<SettingsFrame
								breadcrumb="Settings / Integrations / Slack"
								back="Integrations"
							>
								<SlackIntegrationReplica
									optionRows={
										<LinkRow
											Icon={DoorOpen}
											title="Welcome messages"
											description="Greets people who join your channels or workspace."
											status={welcomeStatus(a.channelOn, a.communityOn)}
											onOpen={() => undefined}
										/>
									}
								/>
							</SettingsFrame>
						</Cell>
						<Cell className={COLUMN_B}>
							<SettingsFrame
								breadcrumb="Settings / Integrations / Slack"
								back="Integrations"
							>
								<SlackIntegrationReplica
									optionRows={
										<>
											<LinkRow
												Icon={DoorOpen}
												title="Channel welcome"
												description="Greets new people in your channels."
												status={onOff(b.channelOn)}
												onOpen={() => setBTab("channel")}
											/>
											<LinkRow
												Icon={MessageUser}
												title="Community welcome"
												description="DMs everyone who joins this Slack workspace."
												status={onOff(b.communityOn)}
												onOpen={() => setBTab("community")}
											/>
										</>
									}
								/>
							</SettingsFrame>
						</Cell>
						<Cell className={COLUMN_C}>
							<SettingsFrame
								breadcrumb="Settings / Integrations / Slack (collapsed)"
								back="Integrations"
							>
								<SlackIntegrationReplica
									section={
										<WelcomeInlineSection
											s={c}
											openRow={cOpenTop}
											setOpenRow={setCOpenTop}
										/>
									}
								/>
							</SettingsFrame>
						</Cell>
					</Row>
					<Row>
						<Cell className={COLUMN_A}>
							<SettingsFrame
								breadcrumb="Settings / Integrations / Slack / Welcome messages"
								isNew
								back="Slack"
							>
								<div className="space-y-4">
									<SettingsHeader
										title="Welcome messages"
										titleSuffix={<NewBadge />}
										subtitle="Greet people who join your shared channels or Slack workspace."
									/>
									<ChannelWelcomeStacked s={a} />
									<CommunityWelcomeStacked s={a} />
								</div>
							</SettingsFrame>
						</Cell>
						<Cell className={COLUMN_B}>
							<SettingsFrame
								breadcrumb="Settings / Integrations / Slack / Welcome messages"
								isNew
								back="Slack"
								columnClassName="max-w-[1120px]"
							>
								<WelcomeSplitSubpage s={b} tab={bTab} setTab={setBTab} />
							</SettingsFrame>
						</Cell>
						<Cell className={COLUMN_C}>
							<SettingsFrame
								breadcrumb="Settings / Integrations / Slack (channel welcome expanded)"
								back="Integrations"
							>
								<SlackIntegrationReplica
									section={
										<WelcomeInlineSection
											s={c}
											openRow={cOpenBottom}
											setOpenRow={setCOpenBottom}
										/>
									}
								/>
							</SettingsFrame>
						</Cell>
					</Row>
				</div>
			</div>
		</div>
	)
}
